/**
 * identity-service JWT store (dao-suite).
 *
 * Story: uid-001-dao-suite-jwt-auth-migration (unblocks frontend-001-9f-c).
 *
 * Holds the identity-service ES256 access JWT (uid-001-5) IN MEMORY ONLY, so the
 * member suite can present it as `Authorization: Bearer <jwt>` to oracle-bridge /
 * blog-service. Mirrors the security posture of `@hello-world-dao/auth`'s
 * `tokenManager`: the token lives in a module-scoped variable, is never written
 * to `localStorage` / `sessionStorage` / a JS-readable cookie, and is therefore
 * cleared on page refresh (XSS cannot exfiltrate it from storage).
 *
 * **Backward-compatibility.** When NO token is set (the default until the
 * identity-service login flow populates it), `memberAuthenticatedFetch` adds no
 * Authorization header and the request rides the existing session cookie exactly
 * as before. The migration is therefore additive: the suite keeps working on
 * cookies, and starts presenting a JWT only once one is stored.
 *
 * The login flow (uid-001-3b front-door + cross-domain handoff) calls
 * `setIdentityJwt()` after exchanging the one-time code for a JWT; `logout`
 * calls `clearIdentityJwt()`. That login wiring is a separate prerequisite
 * (mirrors the dao-admin migration, which shipped the store + transport dark and
 * deferred the login-front-door repoint) — see the story's AC3 scope delta.
 */

/** In-memory identity-service access JWT. Never persisted. */
let identityJwt: string | null = null;

/**
 * Store the identity-service access JWT (after login / token exchange).
 * Pass null/empty to clear.
 */
export function setIdentityJwt(token: string | null): void {
  identityJwt = token && token.length > 0 ? token : null;
}

/**
 * Get the current identity-service access JWT, or null when none is set
 * (cookie-only mode — the backward-compatible default).
 */
export function getIdentityJwt(): string | null {
  return identityJwt;
}

/** Clear the stored identity-service JWT (logout / session expiry). */
export function clearIdentityJwt(): void {
  identityJwt = null;
}

/** True when an identity-service JWT is currently available to send. */
export function hasIdentityJwt(): boolean {
  return identityJwt !== null;
}
